import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProjectV2ServiceService } from '../../../shared/services/project-v2-service.service';
import { finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { BrandService } from '../../../shared/services/brand.service';
import { AuthService } from '../../../shared/services/auth.service';
import { UserService } from '../../../shared/services/user.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import { CryptoService } from '../../../shared/services/crypto.service';
import { SharedModule } from '../../../sub-modules/SharedModule';

@Component({
  selector: 'mtm-milestone-comment',
  styleUrl: './milestone-comment.component.scss',
  templateUrl: './milestone-comment.component.html',
  standalone: true,
  imports: [CommonModule, SharedModule],
})
export class MilestoneCommentComponent implements OnInit, OnDestroy {
  private readonly projectService: ProjectV2ServiceService = inject(ProjectV2ServiceService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly userService: UserService = inject(UserService);
  private readonly brandService: BrandService = inject(BrandService);

  @Input() milestoneId: string;
  @Input() isGlobalView: boolean = false;
  @Input() isMilestone: boolean = false;

  ngUnsubscribe = new Subject();

  comments: any;

  newComment: string;
  newReplyComment: string;
  replyId: string;

  authUser: any;
  participants: any = [];

  commentLoading: boolean = false;
  replyLoading: boolean = false;

  editedCommentId: string;
  wid: string | null = null;
  dc: string | null = null;

  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly route: ActivatedRoute,
    private readonly cryptoService: CryptoService
  ) {}

  ngOnInit(): void {
    this.authUser = this.authService.getAuthUser();
    this.getCompanyUsers();
    this.getComments();
    this.route.queryParams.subscribe((params) => {
      this.wid = this.cryptoService.decryptUsingAES256(params['wid']) || null;
      this.dc = this.cryptoService.decryptUsingAES256(params['dc']) || null;
    });
  }

  ngOnDestroy(): void {
    if (this.isMilestone) {
      this.onSendNewComment();
    }
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  getCompanyUsers() {
    const brand = this.authService.getUserBrand();
    if (!brand?.id) {
      this.participants = [];
      return;
    }
    // Brand payloads no longer carry usernamesRoles — resolve membership via the brand-users endpoint.
    forkJoin([this.userService.getUsersByCompanyId(this.authUser.companyId), this.brandService.getBrandUsers(brand.id)])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: ([companyUsers, brandUsers]) => {
          const brandUsernames = new Set((brandUsers ?? []).map((b) => b.username));
          this.participants = (companyUsers ?? []).filter((u) => brandUsernames.has(u.username));
        },
      });
  }

  getComments() {
    if (!this.milestoneId) {
      return;
    }
    this.projectService
      .getMilestoneComment(this.milestoneId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: any) => {
          this.comments = res.sort((commentA: any, commentB: any) => commentA.dateTime - commentB.dateTime);
        },
      });
  }

  getReplyComments() {
    if (!this.milestoneId) {
      return;
    }
    this.projectService
      .getMilestoneCommentReply(this.replyId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: any) => {
          const parent = this.comments.find((c) => c.id == this.replyId);
          parent.replies = res.sort((commentA: any, commentB: any) => commentA.dateTime - commentB.dateTime);
          parent.replyCount = res.length;
        },
      });
  }

  expandReply(comment) {
    this.replyId = comment.id == this.replyId ? '' : comment.id;
    this.newReplyComment = '';
    this.newComment = '';
    if (this.replyId && comment.replies.length != comment.replyCount) {
      this.getReplyComments();
    }
  }

  onSendNewComment(parentId = null) {
    if (!parentId && !this.newComment) {
      return;
    }
    if (parentId && !this.newReplyComment) {
      return;
    }
    const comment = parentId ? this.newReplyComment : this.newComment;
    const emails = this.newComment.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g) || [];
    const taggedUsers = [...new Set(emails)];
    const data = {
      campaignId: this.dc,
      workspaceId: this.wid,
      milestoneId: this.milestoneId,
      parentId: parentId,
      username: this.authUser.username,
      commentText: comment,
      taggedUsers: taggedUsers,
      files: [],
    };
    this.projectService
      .createMilestoneComment(this.milestoneId, data)
      .pipe(finalize(() => {}))
      .subscribe({
        next: (res) => {
          if (parentId) {
            this.newReplyComment = '';
            this.getReplyComments();
          } else {
            this.newComment = '';
            this.getComments();
          }
        },
        error: (err) => console.error('Error while saving comment:', err),
      });
  }

  getUser(email) {
    return this.participants.find((user) => user.username === email) || {};
  }

  getUserInfo(email) {
    return (
      (this.participants.find(user => user.username === email)?.fullName?.trim() ||
        email?.toLowerCase() ||
        '')
    );
  }

  editComment(comment) {
    this.editedCommentId = comment.id;
  }

  updateComment(comment) {
    this.editedCommentId = null;
    this.projectService
      .editMilestoneComment(comment.id, comment)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          console.log(res);
        },
      });
  }

  deleteComment(comment) {
    if (comment.parentId == this.milestoneId) {
      this.comments = this.comments.filter((c) => c.id != comment.id);
    } else {
      const parentComment = this.comments.find((c) => c.id == comment.parentId);
      parentComment.replies = parentComment.replies.filter((c) => c.id != comment.id);
      parentComment.replyCount--;
    }
    this.projectService
      .deleteMilestoneComment(comment.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          console.log(res);
        },
      });
  }

  closeModal() {
    this.activeModal.close(this.comments);
    this.activeModal.dismiss();
  }
}
